import React, { useEffect, useId, useState } from "react";
import { api } from "@/lib/api";
import { ChevronDown } from "lucide-react";

// First verified RU-labelled geography catalog slice; not a claim of worldwide
// coverage. Replace/expand with a versioned local gazetteer in a later build.
// Other places may still be entered in Russian so no existing draft is blocked.
export const GEOGRAPHY_RU = {
  "Россия": {
    "Новосибирская область": [
      "Барабинск", "Бердск", "Болотное", "Искитим", "Карасук", "Каргат",
      "Куйбышев", "Купино", "Новосибирск", "Обь", "Татарск",
      "Тогучин", "Черепаново", "Чулым",
    ],
    "Москва": ["Москва"],
    "Московская область": ["Балашиха", "Красногорск", "Мытищи", "Подольск"],
    "Санкт-Петербург": ["Санкт-Петербург"],
    "Ленинградская область": ["Всеволожск", "Выборг", "Гатчина"],
  },
  "Италия": {
    "Ломбардия": [
      "Бергамо", "Брешиа", "Бусто-Арсицио", "Варесе", "Виджевано",
      "Галларате", "Комо", "Кремона", "Лекко", "Леньяно", "Лоди",
      "Мантуя", "Милан", "Монца", "Павия", "Ро", "Сесто-Сан-Джованни", "Сондрио",
    ],
    "Лацио": ["Витербо", "Латина", "Рим"],
  },
  "Литва": {
    "Вильнюсский уезд": ["Вильнюс", "Неменчине", "Тракай", "Электренай", "Ширвинтос"],
    "Каунасский уезд": ["Йонава", "Каунас", "Кедайняй"],
  },
};

// ISO 3166-1 alpha-2 codes. Country labels come from the browser's Russian
// locale registry, never from machine translation or external API requests.
// This is a complete selection foundation, NOT the worldwide region/city catalog.
const ISO_COUNTRY_CODES = "AD,AE,AF,AG,AI,AL,AM,AO,AQ,AR,AS,AT,AU,AW,AX,AZ,BA,BB,BD,BE,BF,BG,BH,BI,BJ,BL,BM,BN,BO,BQ,BR,BS,BT,BV,BW,BY,BZ,CA,CC,CD,CF,CG,CH,CI,CK,CL,CM,CN,CO,CR,CU,CV,CW,CX,CY,CZ,DE,DJ,DK,DM,DO,DZ,EC,EE,EG,EH,ER,ES,ET,FI,FJ,FK,FM,FO,FR,GA,GB,GD,GE,GF,GG,GH,GI,GL,GM,GN,GP,GQ,GR,GS,GT,GU,GW,GY,HK,HM,HN,HR,HT,HU,ID,IE,IL,IM,IN,IO,IQ,IR,IS,IT,JE,JM,JO,JP,KE,KG,KH,KI,KM,KN,KP,KR,KW,KY,KZ,LA,LB,LC,LI,LK,LR,LS,LT,LU,LV,LY,MA,MC,MD,ME,MF,MG,MH,MK,ML,MM,MN,MO,MP,MQ,MR,MS,MT,MU,MV,MW,MX,MY,MZ,NA,NC,NE,NF,NG,NI,NL,NO,NP,NR,NU,NZ,OM,PA,PE,PF,PG,PH,PK,PL,PM,PN,PR,PS,PT,PW,PY,QA,RE,RO,RS,RU,RW,SA,SB,SC,SD,SE,SG,SH,SI,SJ,SK,SL,SM,SN,SO,SR,SS,ST,SV,SX,SY,SZ,TC,TD,TF,TG,TH,TJ,TK,TL,TM,TN,TO,TR,TT,TV,TW,TZ,UA,UG,UM,US,UY,UZ,VA,VC,VE,VG,VI,VN,VU,WF,WS,YE,YT,ZA,ZM,ZW".split(",");
const COUNTRY_NAME_OVERRIDES = { RU: "Россия", US: "США", GB: "Великобритания", BY: "Беларусь", CZ: "Чехия" };
const COUNTRY_FALLBACK_RU = [
  "Австрия", "Армения", "Беларусь", "Бельгия", "Великобритания",
  "Германия", "Грузия", "Испания", "Италия", "Казахстан",
  "Латвия", "Литва", "Нидерланды", "Польша", "Россия",
  "Словакия", "США", "Украина", "Франция", "Чехия", "Эстония",
];
function buildCountrySuggestionsRu() {
  if (typeof Intl.DisplayNames !== "function") return COUNTRY_FALLBACK_RU;
  try {
    const names = new Intl.DisplayNames(["ru"], { type: "region" });
    return [...new Set(ISO_COUNTRY_CODES.map((code) => COUNTRY_NAME_OVERRIDES[code] || names.of(code))
      .filter((name) => name && /[А-Яа-яЁё]/u.test(name) && russianPlaceName(name)))].sort((a, b) => a.localeCompare(b, "ru"));
  } catch {
    return COUNTRY_FALLBACK_RU;
  }
}
export const COUNTRY_SUGGESTIONS_RU = buildCountrySuggestionsRu();
const COUNTRY_TO_ISO = new Map(ISO_COUNTRY_CODES.map((code) => [COUNTRY_NAME_OVERRIDES[code] || (typeof Intl.DisplayNames === "function" ? new Intl.DisplayNames(["ru"], { type: "region" }).of(code) : code), code]));

// This deliberately validates human-facing writing, not geographic membership:
// a city absent from today's local catalog must remain possible to save.
export function russianPlaceName(value) {
  return !value || /^[А-Яа-яЁё0-9\s.()\-–—№]+$/u.test(value.trim());
}

const STYLE = "w-full min-h-11 rounded-xl border border-[#826751]/25 bg-white/90 px-3 py-2.5 text-sm text-[#3B2C22] outline-none focus:border-[#86634B] focus-visible:ring-2 focus-visible:ring-[#826751]/20";
const KNOWN_LATIN = {
  vilnius: "Вильнюс", milano: "Милан", milan: "Милан",
  russia: "Россия", lithuania: "Литва", italy: "Италия",
  novosibirsk: "Новосибирск", karasuk: "Карасук",
};

function PlaceField({ label, value, onChange, choices, listId, placeholder, disabled = false, testId, remoteLookup }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [remote, setRemote] = useState({ items: [], status: "idle" });
  useEffect(() => {
    if (!open || !remoteLookup || disabled) return;
    let alive = true;
    setRemote({ items: [], status: "loading" });
    const timer = setTimeout(() => {
      remoteLookup(search).then((items) => {
        if (alive) setRemote({ items, status: "success" });
      }).catch(() => {
        if (alive) setRemote({ items: [], status: "unavailable" });
      });
    }, 180);
    return () => { alive = false; clearTimeout(timer); };
  }, [open, search, remoteLookup, disabled]);
  const fieldId = useId().replace(/:/g, "");
  const query = search.trim().toLocaleLowerCase("ru");
  const isSearchValid = russianPlaceName(search);
  const isSavedValid = russianPlaceName(value);
  const suggestedName = KNOWN_LATIN[query] || null;
  const latinSuggestion = suggestedName && choices.includes(suggestedName) ? suggestedName : null;
  // The country list is small enough to render in full (ISO 3166-1).
  // The dialog already scrolls: truncating it at 60 made later countries unreachable.
  // Region/city suggestions retain their lightweight cap until server-side lookup.
  const allChoices = remote.status === "success" && remote.items.length
    ? [...new Set([...remote.items, ...choices])] : choices;
  const matched = allChoices
    .filter((name) => name.toLocaleLowerCase("ru").includes(query))
    .slice(0, testId === "pm-geo-country" ? choices.length : 12);
  const exact = matched.find((name) => name.toLocaleLowerCase("ru") === query);
  const canUseManual = Boolean(query && isSearchValid && matched.length === 0 && !latinSuggestion);
  const visible = !disabled && open;

  const choose = (name) => {
    onChange(name);
    setOpen(false);
    setSearch("");
  };
  const close = () => {
    setOpen(false);
    setSearch("");
  };

  return (
    <div className="relative min-w-0 space-y-1.5" onBlur={(event) => {
      if (!event.currentTarget.contains(event.relatedTarget)) close();
    }}>
      <span id={fieldId + "-label"} className="block text-xs text-[#755F4E]">{label}</span>
      {/* A non-text trigger keeps Safari's saved address AutoFill away from the visible geographic value.
          The searchable input below has a neutral purpose; only choosing an option commits a value. */}
      <button type="button" disabled={disabled} role="combobox"
        aria-haspopup="dialog" aria-controls={listId + "-panel"} aria-expanded={visible}
        aria-labelledby={fieldId + "-label"}
        className={STYLE + " flex items-center justify-between gap-2 text-left disabled:opacity-50" +
          (isSavedValid ? "" : " border-red-500")}
        data-testid={testId} onClick={() => {
          if (open) close();
          else { setSearch(value || ""); setOpen(true); }
        }}>
        <span className={"min-w-0 truncate " + (!value ? "text-[#806B5C]" : "")}>{value || placeholder}</span>
        <ChevronDown aria-hidden="true" size={20} strokeWidth={2}
          className={"h-5 w-5 shrink-0 text-[#765B45] transition-transform duration-200 " + (visible ? "rotate-180" : "")} />
      </button>
      {visible && <div id={listId + "-panel"} role="dialog" aria-label={"Выбрать: " + label}
        className="absolute left-0 right-0 top-full z-30 mt-1 rounded-xl border border-[#826751]/25 bg-[#FFFCF8] p-2 shadow-[0_9px_22px_rgba(66,48,36,0.12)]"
        data-testid={testId + "-suggestions"}>
        <input type="search" name={"pm-place-query-" + fieldId} autoComplete="off" autoFocus
          aria-label="Поиск по справочнику" aria-autocomplete="list" aria-controls={listId}
          aria-expanded={visible} className={STYLE} value={search}
          onChange={(event) => setSearch(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") { event.preventDefault(); close(); }
            if (event.key === "Enter") {
              event.preventDefault();
              if (exact) choose(exact);
              else if (latinSuggestion) choose(latinSuggestion);
              else if (canUseManual) choose(search.trim());
            }
          }} placeholder="Поиск по справочнику" maxLength={100} aria-invalid={!isSearchValid} />
        {testId === "pm-geo-country" && !query && <p className="px-2 pt-2 text-xs text-[#806B5C]"
          data-testid="pm-country-picker-hint">
          Прокручивай список или начни вводить название страны. Доступно вариантов: {matched.length}.
        </p>}
        <div id={listId} role="listbox" aria-label="Варианты из справочника"
          className="mt-1 max-h-52 overflow-y-auto">
          {matched.map((name) => <button type="button" key={name} role="option" aria-selected={name === value}
            className={"block min-h-11 w-full rounded-lg px-3 py-2 text-left text-sm text-[#4A382B] hover:bg-[#F4EADD] focus-visible:bg-[#F4EADD] focus-visible:outline-none" +
              (name === value ? " bg-[#F4EADD] font-medium" : "")}
            onPointerDown={(event) => event.preventDefault()} onClick={() => choose(name)}>
            {name}
          </button>)}
          {latinSuggestion && !matched.includes(latinSuggestion) && <button type="button" role="option"
            aria-selected={false} className="block min-h-11 w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[#F4EADD]"
            onPointerDown={(event) => event.preventDefault()} onClick={() => choose(latinSuggestion)}>
            Выбрать «{latinSuggestion}»
          </button>}
          {remoteLookup && remote.status === "loading" && <p className="px-3 py-2 text-xs text-[#806B5C]">Загружаем регионы и города…</p>}
          {canUseManual && <button type="button" role="option" aria-selected={false}
            className="block min-h-11 w-full rounded-lg px-3 py-2 text-left text-sm hover:bg-[#F4EADD]"
            onPointerDown={(event) => event.preventDefault()} onClick={() => choose(search.trim())}>
            Использовать «{search.trim()}»
          </button>}
          {!matched.length && !canUseManual && !latinSuggestion && <p className="px-3 py-2 text-xs text-[#806B5C]">
            {search.trim() ? "Выбери русское название." : "Введи название для поиска."}
          </p>}
        </div>
        {value && <button type="button" className="mt-2 min-h-11 px-3 text-sm text-[#705C4D] underline underline-offset-2"
          data-testid={testId + "-clear"} onClick={() => choose("")}>Очистить выбор</button>}
        {!isSearchValid && <p className="mt-2 text-xs text-[#9A443C]" role="alert">
          Название нужно написать по-русски.{suggestedName ? " Выбери «" + suggestedName + "»." : ""}
        </p>}
      </div>}
      {!open && !isSavedValid && <span className="block text-xs text-[#9A443C]" role="alert">
        Название нужно написать по-русски.
      </span>}
    </div>
  );
}

export default function ProfessionalMapGeographyFields({ country, region, city, onChange }) {
  const id = useId().replace(/:/g, "");
  const iso = COUNTRY_TO_ISO.get(country) || "";
  const [regionId, setRegionId] = useState("");
  const [catalogRegions, setCatalogRegions] = useState([]);
  useEffect(() => {
    setRegionId("");
    setCatalogRegions([]);
    if (!iso) return;
    let alive = true;
    api.get("/professional-map/catalog/regions", { params: { country_iso: iso, limit: 50, q: region || "" } })
      .then(({ data }) => {
        if (!alive) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        setCatalogRegions(items);
        const selected = items.find((item) => item.name === region);
        if (selected) setRegionId(selected.id);
      }).catch(() => {});
    return () => { alive = false; };
  }, [iso, region]);
  const remoteRegions = React.useCallback(async (q) => {
    if (!iso) return [];
    const { data } = await api.get("/professional-map/catalog/regions", { params: { country_iso: iso, q, limit: 50 } });
    const items = Array.isArray(data?.items) ? data.items : [];
    setCatalogRegions(items);
    return items.map((item) => item.name);
  }, [iso]);
  const remoteCities = React.useCallback(async (q) => {
    if (!regionId) return [];
    const { data } = await api.get("/professional-map/catalog/cities", { params: { region_id: regionId, q, limit: 50 } });
    return Array.isArray(data?.items) ? data.items.map((item) => item.name) : [];
  }, [regionId]);
  const regionMap = GEOGRAPHY_RU[country] || {};
  const regions = Object.keys(regionMap).sort((a, b) => a.localeCompare(b, "ru"));
  const cities = (regionMap[region] || []).slice().sort((a, b) => a.localeCompare(b, "ru"));

  return (
    <section className="mt-4" aria-label="Страна, область или регион, город" data-testid="pm-profile-location">
      <div className="grid gap-3 sm:grid-cols-3">
        <PlaceField label="Страна" value={country} listId={id + "-countries"}
          choices={COUNTRY_SUGGESTIONS_RU} testId="pm-geo-country"
          placeholder="Выбери страну" onChange={(value) => {
            if (value === country) return;
            onChange({ country: value, region: "", city: "" });
          }} />
        <PlaceField label="Область / регион / уезд" value={region}
          listId={id + "-regions"} choices={regions} testId="pm-geo-region"
          remoteLookup={iso ? remoteRegions : undefined}
          placeholder={country ? "Выбери регион" : "Сначала выбери страну"}
          disabled={!country} onChange={(value) => {
            if (value === region) return;
            setRegionId(catalogRegions.find((item) => item.name === value)?.id || "");
            onChange({ region: value, city: "" });
          }} />
        <PlaceField label="Город" value={city} listId={id + "-cities"}
          choices={cities} testId="pm-geo-city"
          remoteLookup={regionId ? remoteCities : undefined}
          placeholder={region || regions.length === 0 ? "Выбери город" : "Сначала выбери регион"}
          disabled={!country || (!region && regions.length > 0 && !city)} onChange={(value) => onChange({ city: value })} />
      </div>
      <p className="mt-2 text-xs leading-5 text-[#806B5C]">
        Начни вводить название, чтобы увидеть подсказки. Если нужного города пока нет в списке,
        напиши его по-русски. География нужна для будущего поиска и размещения страницы.
      </p>
    </section>
  );
}
